import { useState } from 'react';
import { Input } from '@/components/ui/input';
import {
  MONTHS,
  MONTH_LABELS,
  type CalAdj,
  type MonthParams,
  type RuleFormProps,
  majorStrToMinorOpt,
  minorToMajorStr,
  pctStrToNumOpt,
} from '@/lib/pricingRules';

// Per-month % / absolute (€) adjustment editor. Mirrors the local
// string-draft model from DateSpecificRuleForm (the reference form): draft
// state is seeded once from `params`, and the typed `months` map is rebuilt
// and emitted on every keystroke. A month with neither pct nor abs entered
// is omitted from the emitted map — an empty `months: {}` is itself a valid
// (no-op) rule, so no client-side "non-empty" validation is needed here.

interface MonthDraft {
  pct: string;
  abs: string;
}

function draftFor(adj: CalAdj | undefined): MonthDraft {
  return {
    pct: adj?.pct != null ? String(adj.pct) : '',
    abs: minorToMajorStr(adj?.abs_minor),
  };
}

function draftsToMonths(drafts: Record<string, MonthDraft>): Record<string, CalAdj> {
  const months: Record<string, CalAdj> = {};
  for (const m of MONTHS) {
    const key = String(m);
    const d = drafts[key];
    if (!d) continue;
    const adj: CalAdj = {};
    const p = pctStrToNumOpt(d.pct);
    if (typeof p === 'number') adj.pct = p;
    const a = majorStrToMinorOpt(d.abs);
    if (typeof a === 'number') adj.abs_minor = a;
    if (adj.pct !== undefined || adj.abs_minor !== undefined) months[key] = adj;
  }
  return months;
}

export function MonthRuleForm({ params, onChange }: RuleFormProps<MonthParams>) {
  const [drafts, setDrafts] = useState<Record<string, MonthDraft>>(() => {
    const init: Record<string, MonthDraft> = {};
    for (const m of MONTHS) {
      init[String(m)] = draftFor(params.months?.[String(m)]);
    }
    return init;
  });

  function patch(key: string, p: Partial<MonthDraft>) {
    const current = drafts[key] ?? { pct: '', abs: '' };
    const next = { ...drafts, [key]: { ...current, ...p } };
    setDrafts(next);
    onChange({ months: draftsToMonths(next) });
  }

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {MONTHS.map((m) => {
        const key = String(m);
        const d = drafts[key] ?? { pct: '', abs: '' };
        return (
          <div
            key={key}
            className="rounded-[var(--radius-control)] border border-[var(--border-hairline)] bg-[var(--surface-sunken)] p-3"
          >
            <div className="mb-2 text-sm font-semibold text-[var(--text-heading)]">{MONTH_LABELS[m]}</div>
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Adjustment (%)"
                type="number"
                step="0.1"
                value={d.pct}
                onChange={(e) => patch(key, { pct: e.target.value })}
                placeholder="e.g. -15"
              />
              <Input
                label="Absolute (€)"
                type="number"
                step="0.01"
                value={d.abs}
                onChange={(e) => patch(key, { abs: e.target.value })}
                placeholder="optional"
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
